import React, { useEffect, useMemo, useRef, useState } from "react";
import { Save, RotateCcw, Loader2, ChevronDown, ChevronRight, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { useSiteContent } from "@/contexts/SiteContentContext";
import { formatApiErrorDetail } from "@/lib/apiError";
import { getByPath, setByPath } from "@/lib/objectPath";
import { CONTENT_SECTIONS } from "@/data/contentSchema";
import { translations as DEFAULT_TRANSLATIONS } from "@/data/translations";
import { CONTACT as DEFAULT_CONTACT } from "@/data/contact";
import { COURSES as DEFAULT_COURSES } from "@/data/services";

// Resolve the BUILT-IN default value for a given path, so the form
// shows the original text when no override exists yet.
function defaultForPath(path) {
  if (path.startsWith("translations.")) {
    const parts = path.split(".").slice(1); // remove "translations"
    let v = DEFAULT_TRANSLATIONS;
    for (const p of parts) {
      if (v && typeof v === "object" && p in v) v = v[p];
      else return undefined;
    }
    return v;
  }
  if (path.startsWith("contact.")) {
    const key = path.split(".")[1];
    return DEFAULT_CONTACT[key];
  }
  if (path.startsWith("courses.")) {
    const [, key, field] = path.split(".");
    const c = DEFAULT_COURSES.find((x) => x.key === key);
    return c ? c[field] : undefined;
  }
  // Built-in fallbacks for non-translation keys are defined in SiteContentContext
  // defaults — we don't need to read them here; the empty placeholder is fine.
  return undefined;
}

function toEditorValue(v, type) {
  if (v === undefined || v === null) return "";
  if (type === "list") return Array.isArray(v) ? v.join("\n") : String(v);
  if (type === "boolean") return Boolean(v);
  return String(v);
}

function fromEditorValue(raw, type) {
  if (type === "boolean") return Boolean(raw);
  if (type === "list") {
    return String(raw)
      .split("\n")
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
  }
  // text/textarea/color
  return String(raw);
}

const ContentPanel = () => {
  const { authedAxios } = useAuth();
  const { lang } = useLanguage();
  const { data: serverData, merged, refresh } = useSiteContent();

  // Working draft = sparse override doc currently in form
  const [draft, setDraft] = useState(() => serverData || {});
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [open, setOpen] = useState({ brand: true, contact: true, theme: true });
  const initialized = useRef(false);

  // Sync draft when server data becomes available (hydration only).
  useEffect(() => {
    if (!initialized.current && serverData) {
      setDraft(serverData);
      initialized.current = true;
    }
  }, [serverData]);

  const isDirty = useMemo(
    () => JSON.stringify(draft) !== JSON.stringify(serverData || {}),
    [draft, serverData],
  );

  const handleFieldChange = (path, type) => (e) => {
    const raw =
      type === "boolean"
        ? e.target.checked
        : type === "color"
          ? e.target.value
          : e.target.value;
    const cleaned = fromEditorValue(raw, type);
    setDraft((prev) => setByPath(prev, path, cleaned));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const ax = authedAxios();
      await ax.put("/admin/site-content", { data: draft });
      toast.success(lang === "fr" ? "Contenu enregistré" : "Content saved");
      await refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err));
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (
      !window.confirm(
        lang === "fr"
          ? "Réinitialiser tout le contenu aux valeurs par défaut ? Cette action est définitive."
          : "Reset all content to defaults? This cannot be undone.",
      )
    )
      return;
    setResetting(true);
    try {
      const ax = authedAxios();
      await ax.delete("/admin/site-content");
      setDraft({});
      initialized.current = true;
      toast.success(lang === "fr" ? "Contenu réinitialisé" : "Content reset");
      await refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err));
    } finally {
      setResetting(false);
    }
  };

  const renderField = (field) => {
    const label = lang === "fr" ? field.label_fr : field.label_en;
    const currentOverride = getByPath(draft, field.path);
    const builtIn = defaultForPath(field.path);
    const mergedVal = getByPath(merged, field.path);
    const value = toEditorValue(
      currentOverride !== undefined ? currentOverride : mergedVal !== undefined ? mergedVal : builtIn,
      field.type,
    );

    const placeholder =
      field.type === "boolean" || field.type === "color"
        ? undefined
        : builtIn !== undefined && typeof builtIn !== "object"
          ? String(builtIn)
          : "";

    if (field.type === "textarea" || field.type === "list") {
      return (
        <div key={field.path} className="space-y-2">
          <label className="kw-label">{label}</label>
          <textarea
            rows={field.type === "list" ? 5 : 3}
            value={value}
            onChange={handleFieldChange(field.path, field.type)}
            placeholder={placeholder}
            data-testid={`content-field-${field.path}`}
            className="kw-input resize-none w-full"
          />
          {field.hint && (
            <p className="text-[10px] text-[#A19E9C] italic">{field.hint}</p>
          )}
        </div>
      );
    }

    if (field.type === "color") {
      return (
        <div key={field.path} className="space-y-2">
          <label className="kw-label">{label}</label>
          <div className="flex items-center gap-3">
            <input
              type="color"
              value={value || "#000000"}
              onChange={handleFieldChange(field.path, field.type)}
              data-testid={`content-field-${field.path}`}
              className="w-12 h-10 rounded border border-[#F2EBE5]/15 bg-transparent cursor-pointer"
            />
            <input
              type="text"
              value={value}
              onChange={handleFieldChange(field.path, "text")}
              placeholder={placeholder}
              className="kw-input flex-1"
            />
          </div>
        </div>
      );
    }

    if (field.type === "boolean") {
      return (
        <div key={field.path} className="flex items-center justify-between border border-[#F2EBE5]/10 px-4 py-3">
          <label className="text-sm text-[#F2EBE5]">{label}</label>
          <input
            type="checkbox"
            checked={Boolean(value)}
            onChange={handleFieldChange(field.path, field.type)}
            data-testid={`content-field-${field.path}`}
            className="w-5 h-5 accent-[#C5A059]"
          />
        </div>
      );
    }

    // text
    return (
      <div key={field.path} className="space-y-2">
        <label className="kw-label">{label}</label>
        <input
          type="text"
          value={value}
          onChange={handleFieldChange(field.path, field.type)}
          placeholder={placeholder}
          data-testid={`content-field-${field.path}`}
          className="kw-input"
        />
        {field.hint && (
          <p className="text-[10px] text-[#A19E9C] italic">{field.hint}</p>
        )}
      </div>
    );
  };

  return (
    <section data-testid="admin-content-panel" className="space-y-6 pb-32">
      {/* Sticky action bar */}
      <div
        className="sticky top-20 z-20 -mx-6 md:-mx-12 px-6 md:px-12 py-4 bg-[#050505]/95 backdrop-blur-xl border-b border-[#F2EBE5]/10"
        data-testid="content-action-bar"
      >
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="overline kw-accent-line">
              {lang === "fr" ? "Éditeur de contenu" : "Content editor"}
            </p>
            <p className="mt-1 text-xs text-[#A19E9C]">
              {isDirty
                ? lang === "fr"
                  ? "Modifications non enregistrées"
                  : "Unsaved changes"
                : lang === "fr"
                  ? "Tout est à jour"
                  : "All saved"}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <a
              href="/"
              target="_blank"
              rel="noreferrer"
              className="kw-btn-ghost"
              data-testid="content-open-site"
            >
              <ExternalLink className="w-3.5 h-3.5" strokeWidth={1.5} />
              {lang === "fr" ? "Voir le site" : "View site"}
            </a>
            <button
              type="button"
              onClick={handleReset}
              disabled={resetting}
              data-testid="content-reset-btn"
              className="kw-btn-ghost disabled:opacity-50"
            >
              <RotateCcw className="w-3.5 h-3.5" strokeWidth={1.5} />
              {resetting
                ? lang === "fr"
                  ? "…"
                  : "…"
                : lang === "fr"
                  ? "Réinitialiser"
                  : "Reset"}
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || !isDirty}
              data-testid="content-save-btn"
              className="kw-btn-primary disabled:opacity-40"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />
                  {lang === "fr" ? "Enregistrement…" : "Saving…"}
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" strokeWidth={1.5} />
                  {lang === "fr" ? "Enregistrer" : "Save"}
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Sections */}
      {CONTENT_SECTIONS.map((section) => {
        const isOpen = Boolean(open[section.id]);
        const sectionLabel = lang === "fr" ? section.label_fr : section.label_en;
        return (
          <div
            key={section.id}
            className="border border-[#F2EBE5]/10 bg-[#0a0a0a]"
            data-testid={`content-section-${section.id}`}
          >
            <button
              type="button"
              onClick={() =>
                setOpen((prev) => ({ ...prev, [section.id]: !prev[section.id] }))
              }
              data-testid={`content-section-toggle-${section.id}`}
              className="w-full flex items-center justify-between px-6 md:px-8 py-5 hover:bg-[#0f0f0f] transition-colors"
            >
              <span className="font-serif-display text-2xl text-[#F2EBE5]">
                {sectionLabel}
              </span>
              {isOpen ? (
                <ChevronDown className="w-5 h-5 text-[#C5A059]" strokeWidth={1.25} />
              ) : (
                <ChevronRight className="w-5 h-5 text-[#A19E9C]" strokeWidth={1.25} />
              )}
            </button>
            {isOpen && (
              <div className="px-6 md:px-8 pb-8 pt-2 border-t border-[#F2EBE5]/10">
                <div className="grid md:grid-cols-2 gap-6 mt-6">
                  {section.fields.map((f) => renderField(f))}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </section>
  );
};

export default ContentPanel;
